import { useCallback, useRef, useState } from 'react';
import { OnPlaybackChange } from './types';

export type UsePlaybackControllerProps = {
  isPreviewLoading: boolean;
  isPreviewDirty: () => boolean;
  reprocessNow: () => void;
  onPlaybackChange: OnPlaybackChange;
};

/**
 * controls playback, injecting reprocessing requests when the preview is dirty
 * and cannot be played
 */
export default function usePlaybackController({
  isPreviewLoading,
  isPreviewDirty,
  reprocessNow,
  onPlaybackChange,
}: UsePlaybackControllerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const pendingPlayackRef = useRef(false);

  const handleIsPlayingChange = useCallback(
    (newIsPlaying: boolean): void => {
      setIsPlaying(newIsPlaying);
      onPlaybackChange(newIsPlaying);
    },
    [onPlaybackChange],
  );

  const handlePlaybackChange = useCallback(
    (newIsPlaying: boolean) => {
      if (newIsPlaying && !isPlaying) {
        if (isPreviewLoading) {
          pendingPlayackRef.current = true;
        } else if (isPreviewDirty()) {
          reprocessNow();
        } else {
          handleIsPlayingChange(newIsPlaying);
        }
      } else {
        handleIsPlayingChange(newIsPlaying);
      }
    },
    [
      handleIsPlayingChange,
      isPlaying,
      isPreviewDirty,
      isPreviewLoading,
      reprocessNow,
    ],
  );

  const resumePendingPlayback = useCallback(() => {
    if (pendingPlayackRef.current && !isPlaying) {
      if (!isPreviewDirty()) {
        pendingPlayackRef.current = false;
        handleIsPlayingChange(true);
      } else {
        reprocessNow();
      }
    }
  }, [handleIsPlayingChange, isPlaying, isPreviewDirty, reprocessNow]);

  const clearPendingPlayback = useCallback(() => {
    pendingPlayackRef.current = false;
  }, []);

  const isPendingPlayback = useCallback(() => pendingPlayackRef.current, []);

  return {
    clearPendingPlayback,
    isPendingPlayback,
    isPlaying,
    handlePlaybackChange,
    resumePendingPlayback,
  };
}
